import React, {useCallback, useEffect, useMemo, useState} from 'react';
import cx from 'classnames';
import styles from './styles.module.scss';
import {
    Button,
    ChooseDropdown,
    DropdownItem,
    DropdownV2,
    Flex,
    Icon,
    PriorityIcon,
    Text,
    useKeyboardNavigation,
    Link
} from '@priority-software/priority-style-react';
import {Form, Record} from '../../../../stores/Form';
import {AdvancedSearches} from '../../../Form/components/AdvancedSearch/AdvancedSearches';
import {isRTL} from '../../../../Localization';
import {texts} from '../../texts';
import {TriggerButton} from '../TriggerButton';
import {ModernTooltip} from '../../../../components/Common/Tooltip/ModernTooltip';
import {AutoPlanningDlg} from '../AutoPlanningDlg/AutoPlanningDlg';
import {DropdownItemData} from '@priority-software/priority-style-react/dist/components/DropdownV2/ChooseDropdown/ChooseDropdown';
import {Point} from '../Table/Point';
import {addDocumentsToDeliveryPlan, refetchInTheBackground} from '../../actions/action';
import {ADPFetchMax, ColumnsNamesForRoutes, LOADADVDISTRDATA} from '../../constants';
import {
    setIsNavigationEnabled,
    startProcWithActivationSource
} from '../../../../_shared/GWTBridge/invoke/genPage';
import {ActivationSource} from '../../../../constants/ActivationSource';
import {RouteType} from '../../types';
import {getRecordData} from '../../utils';

type Props = {
    componentClasses?: string;
    form?: Form;
    records?: Record[];
    toggleShowLocations?: () => void;
    onOpenEditDialog: () => void;
    selectedPlannedRows: Record[];
    unselectAll: () => void;
    portalId?: string;
    setIsScreenLocked: (value: boolean) => void;
    reloadForms: (date: string) => void;
    routes: RouteType[];
    routeTitleFields: string[];
    zIndex?: number;
    assignToRouteHandler: (route: DropdownItemData) => Promise<void>;
};

enum SettingsValue {
    group = 'group',
    fields = 'fields'
}

const data: DropdownItemData[] = [
    //{label: texts.group_by, value: SettingsValue.group, icon: PriorityIcon.groupBy},
    {label: texts.choose_fields_to_view, value: SettingsValue.fields}
];

export const PlannedHeader = ({
    componentClasses,
    selectedPlannedRows,
    form,
    records,
    toggleShowLocations,
    portalId,
    onOpenEditDialog,
    setIsScreenLocked,
    reloadForms,
    unselectAll,
    routes,
    routeTitleFields,
    zIndex,
    assignToRouteHandler
}: Props) => {
    const [isOpenSettings, setIsOpenSettings] = useState(false);
    const [isShowLocations, setShowLocations] = useState(false);
    const [isAutoPlanOpen, setIsAutoPlanOpen] = useState<boolean>(false);
    const {focusedIndex, setFocusedIndex, handleNavigationKeys} = useKeyboardNavigation({
        isOpen: true,
        optionsCount: data?.length || 0
    });

    const rtl = isRTL();

    useEffect(() => {
        window.addEventListener('keydown', handleNavigationKeys);
        return () => window.removeEventListener('keydown', handleNavigationKeys);
    }, [handleNavigationKeys, data]);

    const handleSettings = () => {
        setIsOpenSettings(prev => !prev);
        setFocusedIndex(0);
    };
    const handleCloseSettings = () => {
        setIsOpenSettings(false);
    };

    const handleGroupBy = () => {
        console.log('handleGroupBy');
    };

    const onSelect = (item: DropdownItemData) => {
        switch (item.value) {
            case SettingsValue.group: {
                handleGroupBy();
                break;
            }
            case SettingsValue.fields: {
                onOpenEditDialog();
                break;
            }
        }
        setIsOpenSettings(false);
    };

    const renderRouteItem = (routeData: DropdownItemData) => {
        const {data, label} = routeData;
        const color = (data as any)?.color;
        return (
            <Flex justifyContent="flex-start">
                <Point className={styles.moveToItemPoint} color={color} />
                <Text skin="paragraph-2" componentClasses={styles.moveToItemLabel}>
                    {label}
                </Text>
            </Flex>
        );
    };

    const routesData = useMemo((): DropdownItemData[] => {
        const records = routes
            ?.filter(route => {
                const {data} = route;
                return !data.isNew;
            })
            ?.map(item => {
                const {color, data} = item;
                const labelPart1 = getRecordData(data, routeTitleFields?.[0]);
                const labelPart2 = getRecordData(data, routeTitleFields?.[1]);
                const value = getRecordData(data, ColumnsNamesForRoutes.DLVTRACKNUM);

                return {
                    label: `${labelPart1} ${labelPart2}`,
                    value,
                    data: {color: color}
                };
            });

        return records;
    }, [routeTitleFields, routes]);

    const onToggleLocations = useCallback(() => {
        setShowLocations(prev => !prev);
        toggleShowLocations && toggleShowLocations();
    }, []);

    const assignToDeliveryHandler = async (date: string) => {
        setIsScreenLocked(true);
        setIsAutoPlanOpen(false);
        await addDocumentsToDeliveryPlan(date, selectedPlannedRows);
        await reloadForms(date);
        setIsScreenLocked(false);
    };

    const deliveryDateCol = form?.getColumnByName(ColumnsNamesForRoutes.DISTRDATE);

    const loadDocumentsHandler = () => {
        startProcWithActivationSource(LOADADVDISTRDATA, ActivationSource.ADP);
    };

    const clearFilters = async () => {
        setIsScreenLocked(true);
        form?.resetFilters();
        await refetchInTheBackground(form);
        setIsScreenLocked(false);
    };

    const clearSelected = () => {
        unselectAll();
    };

    const handleAutoPlanOpen = useCallback((isOpen: boolean) => {
        setIsNavigationEnabled(!isOpen);
        setIsAutoPlanOpen(isOpen);
    }, []);

    return (
        <div>
            <Flex
                justifyContent="space-between"
                componentClasses={cx(styles.wrapper, styles.lightBackground, componentClasses)}
            >
                <Flex
                    justifyContent="flex-start"
                    style={{gap: 8}}
                    componentClasses={cx(styles.topSection, componentClasses)}
                >
                    <div className={styles.planned}>
                        <Icon icon={PriorityIcon.clipboard} />
                        <Text skin="paragraph-2">{texts.planned}</Text>
                        <div>{records.length}</div>
                    </div>
                    <Button skin={'secondary'} onClick={loadDocumentsHandler}>
                        {texts.load_data}
                    </Button>

                    {form && (
                        <AdvancedSearches
                            mainSearchButton
                            currentForm={form}
                            filterButtonSkin="text"
                            usePortal
                            portalSelector={portalId}
                            withShortcut={false}
                            maxResults={ADPFetchMax}
                            tooltipText={texts.saved_searches_tooltip}
                            useSearchNameAsTooltip
                            savedSearchesWrapperCalss={styles.savedSearchesWrapper}
                        />
                    )}
                    <ModernTooltip
                        tooltip={texts.clear_search}
                        arrowOffset={8}
                        position="top-start"
                    >
                        <Icon
                            icon={PriorityIcon.clearFilters}
                            hoverable
                            size="small"
                            iconColor="primary-700"
                            onClick={clearFilters}
                        />
                    </ModernTooltip>
                </Flex>
                <div className={styles.settingsContainer}>
                    <DropdownV2
                        isOpen={isOpenSettings}
                        onClose={handleCloseSettings}
                        openingSide={'leftSide'}
                        width="fullWidth"
                        contentComponentClasses={styles.dropdownContent}
                        trigger={
                            <ModernTooltip
                                tooltip={texts.settings_planned}
                                maxWidth={600}
                                arrowOffset={8}
                                position="top-start"
                                tooltipOffset={8}
                            >
                                <Icon
                                    icon={PriorityIcon.settings}
                                    hoverable
                                    iconColor="primary-700"
                                    onClick={handleSettings}
                                />
                            </ModernTooltip>
                        }
                        usePortal
                        portalSelector={portalId}
                    >
                        {data.map((item, index) => (
                            <DropdownItem
                                key={index}
                                data={item}
                                onSelect={onSelect}
                                isFocused={index === focusedIndex}
                                itemBorder
                            />
                        ))}
                    </DropdownV2>
                </div>
            </Flex>
            <Flex
                justifyContent="space-between"
                componentClasses={cx(styles.wrapper, styles.paddingBottom)}
            >
                <Text style={{whiteSpace: 'nowrap'}}>
                    {selectedPlannedRows.length}&nbsp;{texts.selected_docs}
                </Text>
                {selectedPlannedRows?.length ? (
                    <Link wrapText={false} onClick={clearSelected}>
                        {texts.clear_all_selected}
                    </Link>
                ) : null}
                <Flex style={{gap: 8}} justifyContent="flex-end">
                    <ModernTooltip
                        tooltip={texts.auto_planning_tooltip}
                        arrowOffset={8}
                        position="top-start"
                        tooltipOffset={8}
                    >
                        <Button
                            skin="secondary"
                            disabled={!selectedPlannedRows?.length}
                            onClick={() => handleAutoPlanOpen(!isAutoPlanOpen)}
                        >
                            {texts.assign_automatically}
                        </Button>
                    </ModernTooltip>
                    {isAutoPlanOpen && (
                        <AutoPlanningDlg
                            deliveryDateCol={deliveryDateCol}
                            closeDialog={() => handleAutoPlanOpen(false)}
                            onAssign={assignToDeliveryHandler}
                            zIndex={zIndex}
                        />
                    )}
                    <ChooseDropdown
                        componentClasses={styles.dropdown}
                        isRtl={rtl}
                        itemBorder={false}
                        onChange={assignToRouteHandler}
                        noOptionsText={texts.no_options_text}
                        usePortal
                        portalSelector={portalId}
                        defaultFocusedIndex={0}
                        maxScrollHeight={320}
                        searchPlaceholder={texts.search_route}
                        itemContentRenderer={renderRouteItem}
                        data={routesData || []}
                        contentComponentClasses={styles.dropdownContent}
                        trigger={
                            <ModernTooltip
                                tooltip={texts.assign_to_tooltip}
                                maxWidth={600}
                                arrowOffset={8}
                                position="top-start"
                                tooltipOffset={8}
                            >
                                <TriggerButton
                                    value={texts.assign_to_route}
                                    disabled={!selectedPlannedRows?.length}
                                />
                            </ModernTooltip>
                        }
                    ></ChooseDropdown>
                    <ModernTooltip
                        tooltip={texts.show_planned_locations}
                        maxWidth={800}
                        arrowOffset={8}
                        position="top-start"
                        tooltipOffset={8}
                    >
                        <Icon
                            disabled={!selectedPlannedRows?.length}
                            icon={
                                isShowLocations ? PriorityIcon.locationFill : PriorityIcon.location
                            }
                            hoverable
                            iconColor="primary-700"
                            onClick={onToggleLocations}
                        />
                    </ModernTooltip>
                </Flex>
            </Flex>
        </div>
    );
};
